import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Frame } from '@/types/location';
import { PreloadedSequence } from '@/types/almanac';
import { MiniMap } from '@/components/ui/almanac/MiniMap';
import { useAlmanac } from '@/hooks/useAlmanac';
import Player, {
  PlayerProps,
} from '@/components/ui/almanac/FramePlayer/Player';
import Modal from '@/components/ui/scout/modal/Modal';

const NUM_MS_PER_FRAME = 1000;

const PlayerWithModal: React.FC = () => {
  const {
    setActiveLocation,
    sortedSequences,
    activeLocation,
    activeSequenceIndex,
    setActiveSequenceIndex,
    activeFrameIndex,
    setActiveFrameIndex,
  } = useAlmanac();

  const [cachedSequences, setDownloadedSequences] = useState<
    PreloadedSequence[]
  >(new Array(sortedSequences.length).fill(null));

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState(true);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const activeSequence = sortedSequences[activeSequenceIndex];

  const cachedSequence = cachedSequences[activeSequenceIndex];
  const cachedFrame = cachedSequence?.images[activeFrameIndex];
  const [showDateAndDotMenu, setShowDateAndDotMenu] = useState(false);

  const newDrivePathTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSkip = useCallback(
    (direction: 'back' | 'forward') => {
      if (direction === 'forward') {
        setActiveSequenceIndex(prevState => {
          return prevState < sortedSequences.length - 1 ? prevState + 1 : 0;
        });
        setActiveFrameIndex(0);
      } else if (direction === 'back') {
        setActiveSequenceIndex(prevState => {
          return prevState > 0 ? prevState - 1 : prevState;
        });
        setActiveFrameIndex(0);
      }
    },
    [setActiveSequenceIndex, setActiveFrameIndex, sortedSequences.length],
  );

  const skipFrame = useCallback(
    (direction: 'backward' | 'forward') => {
      // Go to the first frame of the next or previous sequence or loop back to the first sequence.
      setActiveFrameIndex(currentFrameIndex => {
        if (direction === 'forward') {
          let newFrameIndex = currentFrameIndex;

          setActiveSequenceIndex(currSequenceIndex => {
            const currentSequence = sortedSequences[currSequenceIndex];
            if (currentFrameIndex < currentSequence.length - 1) {
              // If there are more frames
              newFrameIndex = currentFrameIndex + 1;
              return currSequenceIndex;
            } else {
              // If there no more frames in current sequence
              if (currSequenceIndex < sortedSequences.length - 1) {
                // Go to the next sequence
                newFrameIndex = 0;
                return currSequenceIndex + 1;
              } else {
                // Loop back to the first sequence
                newFrameIndex = 0;
                return 0;
              }
            }
          });

          return newFrameIndex;
        }
        // Backward
        else {
          if (currentFrameIndex > 0) {
            return currentFrameIndex - 1;
          } else {
            let newFrameIndex = currentFrameIndex;
            setActiveSequenceIndex(prevState => {
              newFrameIndex = 0;
              if (prevState > 0) {
                return prevState - 1;
              } else {
                return sortedSequences.length - 1;
              }
            });

            return newFrameIndex;
          }
        }
      });
    },
    [setActiveFrameIndex, sortedSequences, setActiveSequenceIndex],
  );

  const stop = useCallback((updateState: boolean = true) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      if (updateState) {
        setIsPlaying(false);
      }
    }
  }, []);

  const play = useCallback(() => {
    if (intervalRef.current) return;

    setIsPlaying(true);
    intervalRef.current = setInterval(() => {
      skipFrame('forward');
    }, NUM_MS_PER_FRAME);
  }, [skipFrame]);

  // If autoplaying, the date and dot menu show up for a few seconds when we switch to a new drive path.
  useEffect(() => {
    if (!isPlaying) return;
    if (cachedSequence?.status !== 'ready') return;
    if (newDrivePathTimeoutRef.current) {
      clearTimeout(newDrivePathTimeoutRef.current);
    }
    // Show the date and dot menu for 3 seconds
    setShowDateAndDotMenu(true);
    newDrivePathTimeoutRef.current = setTimeout(() => {
      setShowDateAndDotMenu(false);
    }, 3000);
  }, [activeSequenceIndex, isPlaying, cachedSequence?.status]);

  const eventKeyCallback = useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case ' ':
          isPlaying ? stop() : play();
          break;
        case 'f':
          setShowModal(prev => !prev);
          break;
        case 'ArrowLeft':
          skipFrame('backward');
          break;
        case 'ArrowRight':
          skipFrame('forward');
          break;
        case 'Escape':
          // Exit full screen mode if currently fullscreen
          // If not fullscreen, exit player entirely.
          if (showModal === true) {
            setShowModal(false);
          } else {
            setActiveLocation(undefined);
          }
          break;
        default:
          break;
      }
    },
    [isPlaying, stop, play, skipFrame, showModal, setActiveLocation],
  );

  const onDotClick = useCallback(
    (index: number) => {
      setActiveSequenceIndex(index);
      setActiveFrameIndex(0);
    },
    [setActiveSequenceIndex, setActiveFrameIndex],
  );

  useEffect(() => {
    document.addEventListener('keydown', eventKeyCallback, false);

    return () => {
      document.removeEventListener('keydown', eventKeyCallback, false);
    };
  }, [eventKeyCallback]);

  useEffect(() => {
    // if the current sequence is ready, enqueue the following sequence
    if (cachedSequence && cachedSequence.status === 'ready') {
      if (cachedSequences[activeSequenceIndex + 1]) return;

      if (activeSequenceIndex + 1 < sortedSequences.length) {
        cacheSequence(sortedSequences, activeSequenceIndex + 1);
      }
      return;
    }

    cacheSequence(sortedSequences, activeSequenceIndex);
  }, [sortedSequences, activeSequenceIndex, cachedSequences, cachedSequence]);

  useEffect(() => {
    if (cachedSequence) {
      if (cachedSequence.status === 'ready') {
        isPlaying && play();
      } else {
        stop(false);
      }
    }
  }, [
    cachedSequences,
    cachedSequence,
    activeSequenceIndex,
    play,
    stop,
    isPlaying,
  ]);

  const cacheSequence = (
    sortedSequences: Frame[][],
    activeSequenceIndex: number,
  ) => {
    const sequence = sortedSequences[activeSequenceIndex];
    if (!sequence) return;
    sequence.forEach(({ url }, index) => {
      // to preload the images we leverage caching in the browser,
      // if your browser settings are set to no-cache, this will not work as expected.
      const img = new Image();
      img.src = url;

      img.onload = () => {
        setDownloadedSequences(prevState => {
          const copy = [...prevState];
          if (!copy[activeSequenceIndex]) {
            copy[activeSequenceIndex] = {
              status: 'in-flight',
              images: [],
            };
          }

          // this can be changed to any type of algorithm to determine if a sequence is ready
          if (index + 1 >= sequence.length * 0.5) {
            copy[activeSequenceIndex].status = 'ready';
          }

          copy[activeSequenceIndex]['images'][index] = img;
          return copy;
        });
      };

      img.onerror = error => console.log(img.src, 'img error', error);
    });
  };

  useEffect(() => {
    return () => {
      intervalRef.current && clearInterval(intervalRef.current);
    };
  }, []);

  const props: PlayerProps = {
    cachedSequence,
    cachedFrame,
    onDotClick,
    handleSkip,
    stop,
    play,
    isPlaying,
    setShowModal,
    showModal,
    showDateAndDotMenu,
  };

  return (
    <>
      <Modal showModal={showModal} setShowModal={setShowModal} fullScreen>
        <Player {...props} />
        {activeLocation && (
          <MiniMap
            location={activeLocation}
            variant="almanac"
            activeSequence={activeSequence}
            activeFrameIndex={activeFrameIndex}
          />
        )}
      </Modal>
      <Player {...props} />
    </>
  );
};

export default PlayerWithModal;
